import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Button, Input, Modal, message } from '../../ui/ot';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { createPatronImportFile, createPatronPreviewData, getPatronIssueColumns, parsePatronWorkbook, projectPatronImportSheets, validatePatronPreview } from './staff_patron_import_preview';

const importApi = '/library/organization_structure/api/staff/concatenation_import';

function mapServerIssues(errors, sheets) {
    return errors.map(issue => {
        const sheet = sheets.find(item => item.name === issue.sheet_name);
        const rowNumber = Number(issue.row_number);
        return { ...issue, sheetId: sheet?.id, rowId: sheet?.rows.find(row => row.rowNumber === rowNumber)?.id, rowNumber };
    });
}

export default function StaffPatronImportModal({ onQueued = () => {} }) {
    const [open, setOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [schema, setSchema] = useState(null);
    const [sheets, setSheets] = useState([]);
    const [fileName, setFileName] = useState('');
    const [activeId, setActiveId] = useState('');
    const [page, setPage] = useState(1);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState('');
    const [serverIssues, setServerIssues] = useState([]);
    const [checkState, setCheckState] = useState('idle');
    const [checkAttempt, setCheckAttempt] = useState(0);
    const checkDelay = useRef(0);
    const revision = useRef(0);
    const inputRef = useRef(null);
    const submitting = useRef(false);
    const localIssues = useMemo(() => schema ? validatePatronPreview(sheets, schema) : [], [sheets, schema]);
    const issues = [...localIssues, ...serverIssues];
    const activeSheet = sheets.find(sheet => sheet.id === activeId);
    const sheetIssues = issues.filter(issue => issue.sheetId === activeId);
    const totalRows = sheets.reduce((count, sheet) => count + sheet.rows.length, 0);

    useEffect(() => {
        if (!schema || !sheets.length) return;
        const controller = new AbortController();
        const current = revision.current;
        let active = true;
        const timer = setTimeout(async () => {
            setCheckState('checking');
            try {
                const response = await axios.post(`${importApi}/validate`, createPatronPreviewData(sheets, schema), { signal: controller.signal });
                if (!active || current !== revision.current) return;
                if (response.data?.status !== 'success' || !Array.isArray(response.data.validation_errors)) throw new Error(response.data?.message || '資料庫檢查未完成');
                setServerIssues(mapServerIssues(response.data.validation_errors, sheets));
                setCheckState('checked'); setNotice('');
            } catch (error) {
                if (!active || current !== revision.current) return;
                setCheckState('failed');
                setNotice(error.response?.data?.message || error.message || '無法完成資料庫檢查，請重新檢查');
            }
        }, checkDelay.current);
        return () => { active = false; clearTimeout(timer); controller.abort(); };
    }, [sheets, schema, checkAttempt]);

    const show = async () => {
        const current = ++revision.current;
        setOpen(true); setPreviewOpen(false); setBusy(true); setNotice(''); setSchema(null);
        setSheets([]); setFileName(''); setServerIssues([]); setPage(1);
        setCheckState('idle');
        try {
            const response = await axios.get(`${importApi}/schema`);
            if (current !== revision.current) return;
            if (response.data?.status !== 'success') throw new Error(response.data?.message || '無法載入欄位設定');
            setSchema(response.data.data);
        } catch (error) {
            if (current === revision.current) setNotice(error.message || '無法載入欄位設定，請重新開啟');
        } finally {
            if (current === revision.current) setBusy(false);
        }
    };
    const close = () => {
        if (submitting.current) return;
        revision.current++; setOpen(false); setPreviewOpen(false); setBusy(false);
        setSheets([]); setServerIssues([]);
        setCheckState('idle');
    };
    const showPreview = () => { setOpen(false); setPreviewOpen(true); };
    const closePreview = () => {
        if (submitting.current) return;
        setPreviewOpen(false); setOpen(true);
    };
    const selectFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        const current = ++revision.current;
        setBusy(true); setNotice(''); setSheets([]); setServerIssues([]); setFileName(file.name); setCheckState('idle');
        try {
            if (!/\.(xlsx|xls|xlsm)$/i.test(file.name)) throw new Error('請選擇 .xlsx、.xls 或 .xlsm 檔案');
            const buffer = await file.arrayBuffer();
            const parsed = projectPatronImportSheets(parsePatronWorkbook(buffer), schema);
            if (!parsed.some(sheet => sheet.rows.length)) throw new Error('檔案沒有可匯入的讀者資料');
            if (current !== revision.current) return;
            checkDelay.current = 0; setCheckState('pending');
            setSheets(parsed); setActiveId(parsed[0]?.id ?? ''); setPage(1);
            showPreview();
        } catch (error) {
            if (current === revision.current) setNotice(error.message || '無法讀取 Excel 檔案');
        } finally {
            if (current === revision.current) setBusy(false);
        }
    };
    const updateSheet = (update) => {
        revision.current++; checkDelay.current = 3000; setCheckState('pending');
        setSheets(previous => previous.map(sheet => sheet.id === activeId ? update(sheet) : sheet));
        setServerIssues([]); setNotice('');
    };
    const submit = async () => {
        if (submitting.current || checkState !== 'checked' || !schema || !totalRows || issues.length || busy) return;
        submitting.current = true; setBusy(true); setNotice('');
        try {
            const form = new FormData();
            form.append('inputFile', createPatronImportFile(sheets, fileName, schema));
            const response = await axios.post(`${importApi}/read_sample`, form);
            if (response.data?.status !== 'success') {
                const error = new Error(response.data?.message || '無法送出匯入資料');
                error.response = response;
                throw error;
            }
            message.success('已送出匯入，請至「查看匯入進度」確認結果');
            setOpen(false); setPreviewOpen(false); setSheets([]); onQueued();
        } catch (error) {
            const data = error.response?.data;
            setNotice(data?.message || error.message || '無法送出，請稍後重試');
            const returned = mapServerIssues(data?.validation_errors ?? [], sheets);
            setServerIssues(returned);
            if (returned[0]?.sheetId) { setActiveId(returned[0].sheetId); setPage(1); }
        } finally {
            submitting.current = false; setBusy(false);
        }
    };
    const download = async () => {
        try {
            const response = await axios.get(`${importApi}/manual`, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a'); link.href = url; link.download = '讀者匯入範例.xlsx'; link.click();
            URL.revokeObjectURL(url);
        } catch { message.error('無法下載範例，請稍後重試'); }
    };
    const redFrame = { border: '2px solid #dc2626', boxShadow: '0 0 0 1px #dc2626' };
    const headerErrors = new Set();
    const rowErrors = new Map();
    if (activeSheet) sheetIssues.forEach(issue => {
        const columns = getPatronIssueColumns(activeSheet, issue, schema);
        if (!issue.rowId) columns.forEach(column => headerErrors.add(column));
        else {
            if (!rowErrors.has(issue.rowId)) rowErrors.set(issue.rowId, new Set());
            columns.forEach(column => rowErrors.get(issue.rowId).add(column));
        }
    });
    return <>
        <Button onClick={show} disabled={busy || open || previewOpen} style={{ backgroundColor: '#388E3C', color: '#FFFFFF' }}>匯入資料</Button>
        <Modal open={open} onClose={close} close closeText="取消" width={600} title="讀者匯入：選擇檔案" contentStyle={{ padding: 12 }}>
            <p>1. 選擇檔案 → 2. 檢查並修正 → 3. 確認送出</p>
            <p>選檔後立即檢查必填欄位、格式、重複證號及既有讀者資料；有錯誤或比對未完成時無法送出。</p>
            <p>僅顯示並傳送該讀者類型支援的欄位，其餘欄位自動忽略。修正內容後會重新檢查。</p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                <Button onClick={download} disabled={busy}>下載範例</Button>
                <Button onClick={() => inputRef.current?.click()} disabled={busy || !schema}>選擇 Excel 檔案</Button>
                <input aria-label="讀者匯入檔案" type="file" accept=".xlsx,.xls,.xlsm" hidden ref={inputRef} onChange={selectFile} disabled={busy || !schema} />
                <span>{fileName}</span>{busy && <span role="status">處理中…</span>}
            </div>
            {notice && <p role="alert" style={{ color: '#b91c1c' }}>{notice}</p>}
            {!!sheets.length && <Button onClick={showPreview} disabled={busy}>檢查預覽</Button>}
        </Modal>
        <Modal open={previewOpen} onClose={closePreview} close closeText="返回選檔" width="95vw" title="讀者匯入：檢查預覽" contentStyle={{ padding: 12 }}>
            <p>{fileName}</p>
            <p>可直接修改資料，紅框表示欄位有錯誤。非必填欄位可維持空白。</p>
            <p>缺少的單位欄位會自動補上，請填寫完整單位代碼。「原派訓單位」為額外資訊。</p>
            <p role="status" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {checkState === 'checking' && <Loader2 role="img" aria-label="檢查中" className="animate-spin" size={18} />}
                {checkState === 'checked' ? '資料比對完成' : checkState === 'failed' ? '資料庫比對失敗，請重新檢查' : checkState === 'pending' ? '等待重新檢查' : '正在檢查欄位與資料庫既有資料'}
            </p>
            {(checkState === 'failed' || checkState === 'checked') && <Button disabled={busy} onClick={() => { checkDelay.current = 0; setCheckState('pending'); setCheckAttempt(value => value + 1); }}>重新檢查</Button>}
            {notice && <p role="alert" style={{ color: '#b91c1c' }}>{notice}</p>}
            {issues.filter(issue => !issue.sheetId).map((issue, index) => <p key={index} role="alert" style={{ color: '#b91c1c' }}>{issue.message}</p>)}
            {!!sheets.length && <>
                {issues.length > 0 && <p role="status">共 {totalRows} 筆資料，有 {issues.length} 項錯誤，請先修正</p>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                    {sheets.map(sheet => <Button key={sheet.id} disabled={busy} aria-pressed={activeId === sheet.id}
                        style={{ backgroundColor: activeId === sheet.id ? '#4338ca' : '#eef2ff', color: activeId === sheet.id ? '#ffffff' : '#3730a3', border: '1px solid #a5b4fc' }}
                        onClick={() => { setActiveId(sheet.id); setPage(1); }}>
                        {sheet.name}（{sheet.rows.length} 筆）{issues.some(issue => issue.sheetId === sheet.id) ? ' ⚠' : ''}
                    </Button>)}
                </div>
            </>}
            {activeSheet && <fieldset disabled={busy} style={{ border: 0, padding: 0 }}>
                {!!sheetIssues.length && <div role="alert" style={{ maxHeight: 150, overflowY: 'auto', color: '#b91c1c', margin: '12px 0' }}>
                    {sheetIssues.slice(0, 100).map((issue, index) => <div key={index}>第 {issue.rowNumber} 列：{issue.message}</div>)}
                    {sheetIssues.length > 100 && <div>此處顯示前 100 項，各列完整錯誤請查看下方表格。</div>}
                </div>}
                <div style={{ overflowX: 'auto', maxHeight: '45vh' }}>
                    <Table>
                        <TableHeader><TableRow><TableHead>原始列號</TableHead>{activeSheet.headers.map((header, column) => <TableHead key={column} style={{ minWidth: 180 }}>
                            <div aria-label={`標頭 ${column + 1}`} style={{ padding: 8, whiteSpace: 'pre-wrap', border: '2px solid transparent', ...(headerErrors.has(column) ? redFrame : {}) }}>{header}</div>
                        </TableHead>)}<TableHead>檢查結果</TableHead></TableRow></TableHeader>
                        <TableBody>{activeSheet.rows.slice((page - 1) * 25, page * 25).map(row => <TableRow key={row.id}>
                            <TableCell>{row.rowNumber}</TableCell>{row.cells.map((value, column) => <TableCell key={column}>
                                <Input aria-label={`第 ${row.rowNumber} 列 ${activeSheet.headers[column] || `欄位 ${column + 1}`}`} aria-invalid={rowErrors.get(row.id)?.has(column) ?? false} style={rowErrors.get(row.id)?.has(column) ? redFrame : undefined} value={value}
                                    onChange={event => updateSheet(sheet => ({ ...sheet, rows: sheet.rows.map(item => item.id === row.id ? { ...item, cells: item.cells.map((cell, index) => index === column ? event.target.value : cell) } : item) }))} />
                            </TableCell>)}<TableCell style={{ minWidth: 250, color: '#b91c1c' }}>{sheetIssues.filter(issue => issue.rowId === row.id).map((issue, index) => <div key={index}>{issue.message}</div>)}</TableCell>
                        </TableRow>)}</TableBody>
                    </Table>
                </div>
                <div style={{ display: 'flex', gap: 8, margin: '12px 0' }}>
                    <Button disabled={page <= 1 || busy} onClick={() => setPage(value => value - 1)}>上一頁</Button>
                    <span>第 {page} / {Math.max(1, Math.ceil(activeSheet.rows.length / 25))} 頁</span>
                    <Button disabled={page * 25 >= activeSheet.rows.length || busy} onClick={() => setPage(value => value + 1)}>下一頁</Button>
                </div>
            </fieldset>}
            <Button onClick={submit} disabled={busy || checkState !== 'checked' || !schema || !totalRows || issues.length > 0} loading={busy}>確認無誤，送出匯入</Button>
        </Modal>
    </>;
}
